import { createSlackIncomingHook } from '@gonfalon/openapi/operations/createSlackIncomingHook';
import { deleteSlackIncomingHook } from '@gonfalon/openapi/operations/deleteSlackIncomingHook';
import { getSlackIncomingHooks } from '@gonfalon/openapi/operations/getSlackIncomingHooks';
import { updateSlackIncomingHook } from '@gonfalon/openapi/operations/updateSlackIncomingHook';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';

import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';

/**
 * Slack incoming hooks share the Goaltender subscription routes — `/api/v2/integrations/slack` — via
 * the `legacyHandlerMap` in `internal/api2/integrations/routes.go`. The contracts differ in both
 * directions: responses are `reps2.SlackIncomingHookRep`, POST takes a slack-shaped body, and PATCH
 * takes a JSON Patch document.
 *
 * The Go handlers are annotated under the literal path `/api/v2/integrations/slack`, which coexists
 * with the templated `/api/v2/integrations/{integrationKey}` the subscription operations use. So this
 * is a separate module over its own generated operations, not an override widening theirs.
 */

export const getSlackIncomingHooksQuery = createQueryOptions(getSlackIncomingHooks);

/**
 * Exported for the legacy Redux write path in `#actions/integrations` and `#actions/webhooks`, which
 * still owns these mutations while the integrations page is mid-migration (REL-15378). The Redux
 * page reads its own store, so without this the react-query page shows stale data for the length of
 * `staleTime` after any create, edit or delete.
 */
export async function invalidateSlackIncomingHooks() {
  const queryClient = getQueryClient();
  await queryClient.invalidateQueries({ queryKey: getSlackIncomingHooksQuery.partialQueryKey() });
}

export const useCreateSlackIncomingHook = createMutationHook(createSlackIncomingHook, {
  onSuccess: invalidateSlackIncomingHooks,
});

/**
 * `PatchSlackIncomingHookById` runs `jsonpatch.DecodePatch` on the raw body, so `body` is a JSON Patch
 * document rather than a representation. Only name, url, tags and on are patchable.
 *
 * Produce it with `createJsonPatch` (`static/ld/utils/patchUtils.ts`), which is what legacy
 * `updateIntegration` used. Assembling ops by hand drops the `remove` entries that clearing a field
 * needs.
 *
 * Conflicts return `f.ErrorJsonMsg("Optimistic locking error")`: a `{message}` body with no `code`, so
 * the `RESTAPIError` carries `code: 'unknown_error'`. Detect them with `isConflictError` from
 * `@gonfalon/errors/isConflictError`, which tests the status.
 */
export const useUpdateSlackIncomingHook = createMutationHook(updateSlackIncomingHook, {
  onSuccess: invalidateSlackIncomingHooks,
});

export const useDeleteSlackIncomingHook = createMutationHook(deleteSlackIncomingHook, {
  onSuccess: invalidateSlackIncomingHooks,
});
